import { GDTApiErrors } from '@/types';
import { ResponseBase } from '@/types/commons';
import {
  BasicDataGDA,
  CheckNameResponse,
  CreateTeamResponse,
  DTsResultResponse,
  FCupViewTeamResponse,
  FriendCupResponse,
  GroupsResultResponse,
  InitFCup,
  RequestChangeNameFCup,
  RequestGroupsSearch,
  RequestManagePostulantFCup,
  RequestSearchDTs,
  ResponseExpelMemberFCup,
  ResponseManagePostulantFCup,
  ResponseTop10,
  SubscribeGroupResponse,
  SuggestedGroupsResponse,
} from '@/types/friendCup';
import { axiosErrorHandler, TEXTS, URLs } from '@/utils';
import axiosGdt, { axiosGdtWithoutCredentials } from '@/utils/axiosGdt';

export enum ActionTypeManageFCupEnum {
  ACC_ACEPTAR_POSTULACION = 1,
  ACC_RECHAZAR_POSTULACION = 2,
  ACC_ACEPTAR_INVITACION = 3,
  ACC_RECHAZAR_INVITACION = 4,
  ACC_ACEPTAR_POSTULACION_DSD_INV = 5,
}

export const requestNewTeam = axiosErrorHandler(async (groupName: string) => {
  const url = `${URLs.COPA_AMIGOS.CREAR}`;
  const response = await axiosGdt.post<CreateTeamResponse>(url, null, {
    params: { nombreGrupo: groupName },
  });

  return response.data;
}, TEXTS.defaultError);

export const requestChangeName = axiosErrorHandler(
  async (data: RequestChangeNameFCup) => {
    const url = `${URLs.COPA_AMIGOS.CAMBIAR_NOMBRE}`;
    const response = await axiosGdt.post<ResponseBase>(url, null, {
      params: data,
    });

    return response.data;
  },
  TEXTS.defaultError
);

export const getInitFCup = axiosErrorHandler(async () => {
  const url = `${URLs.COPA_AMIGOS.INIT}`;
  const response = await axiosGdt.get<InitFCup>(url);

  return response.data;
}, TEXTS.defaultError);

export const requestRecommendedFCup = axiosErrorHandler(async () => {
  const url = `${URLs.COPA_AMIGOS.GRUPOS_SUGERIDOS}`;
  const response = await axiosGdt.get<SuggestedGroupsResponse>(url, {
    params: { cantMaxResultados: 5 },
  });

  return response.data;
}, TEXTS.defaultError);

export const requestTop10FCup = axiosErrorHandler(async (id: number) => {
  const url = `${URLs.COPA_AMIGOS.TOP10}`;
  const response = await axiosGdtWithoutCredentials.get<ResponseTop10>(
    `${url}/topGral_grupoCA_${id}.json`
  );

  return response.data;
}, TEXTS.defaultError);

export const requestCheckNameAvailability = axiosErrorHandler(
  async (groupName: string) => {
    const url = `${URLs.COPA_AMIGOS.VALIDAR_NOMBRE}`;
    const params = `nombre=${groupName}`;

    const response = await axiosGdt.get<CheckNameResponse | GDTApiErrors>(
      `${url}?${params}`
    );

    return response.data;
  },
  TEXTS.defaultError
);

export const getFullInitFCup = axiosErrorHandler(async () => {
  const [init, recommended] = await Promise.all([
    getInitFCup(),
    requestRecommendedFCup(),
  ]);

  return {
    init,
    recommended,
  };
}, 'Error al obtener datos');

export const requestPostulationFCup = axiosErrorHandler(
  async (idGrupo: number) => {
    const url = `${URLs.COPA_AMIGOS.ENVIAR_SOLICITUD_GRUPO}`;
    const response = await axiosGdt.post<SubscribeGroupResponse>(url, null, {
      params: { idGrupo },
    });

    return response.data;
  },
  TEXTS.defaultError
);

export const requestPostulantManagementFCup = axiosErrorHandler(
  async (data: RequestManagePostulantFCup) => {
    const { TRATAR_POSTULANTE } = URLs.COPA_AMIGOS;
    const { tipoAccion, idGrupo, idUsuario, idParticipante } = data;

    const params = new URLSearchParams({
      tipoAccion: tipoAccion.toString(),
      idGrupo: idGrupo.toString(),
      ...(tipoAccion <= ActionTypeManageFCupEnum.ACC_RECHAZAR_POSTULACION &&
        idParticipante && {
          idParticipante: idParticipante.toString(),
        }),
      ...(tipoAccion ===
        ActionTypeManageFCupEnum.ACC_ACEPTAR_POSTULACION_DSD_INV &&
        idUsuario && {
          idUsuario: idUsuario?.toString(),
        }),
    });

    const response = await axiosGdt.post<ResponseManagePostulantFCup>(
      `${TRATAR_POSTULANTE}?${params}`
    );

    return response.data;
  },
  TEXTS.defaultError
);

export const requestExpelMemberFCup = axiosErrorHandler(
  async ({
    idGrupo,
    idParticipante,
  }: {
    idParticipante: number;
    idGrupo: number;
  }) => {
    const params = new URLSearchParams({
      idParticipante: idParticipante.toString(),
      idGrupo: idGrupo.toString(),
    });

    const response = await axiosGdt.post<ResponseExpelMemberFCup>(
      `${URLs.COPA_AMIGOS.EXPULSAR_MIEMBRO}?${params}`
    );

    return response.data;
  },
  TEXTS.defaultError
);

export const requestSearchGroups = axiosErrorHandler(
  async (data: RequestGroupsSearch) => {
    const url = `${URLs.COPA_AMIGOS.BUSCAR_GRUPO}`;
    const params = new URLSearchParams({
      nombreGrupo: String(data.nombreGrupo),
      nombreCreador: String(data.nombreCreador),
      apellidoCreador: String(data.apellidoCreador),
      paginaActual: String(data.paginaActual),
      cantPorPagina: String(data.cantPorPagina),
    });
    const response = await axiosGdt.get<GroupsResultResponse>(url, {
      params,
    });

    return response.data;
  },
  TEXTS.defaultError
);

export const requestSearchDTs = axiosErrorHandler(
  async (data: RequestSearchDTs) => {
    const url = `${URLs.COPA_AMIGOS.BUSCAR_DTS}`;
    const params = new URLSearchParams({
      search: String(data.search),
      idGrupo: String(data.idGrupo),
    });
    const response = await axiosGdt.get<DTsResultResponse>(url, {
      params,
    });

    return response.data;
  },
  TEXTS.defaultError
);

export const requestFCupViewTeam = axiosErrorHandler(
  async (groupId: number) => {
    const url = `${URLs.COPA_AMIGOS.VER_COPA_AMIGOS}`;
    const response = await axiosGdt.get<FCupViewTeamResponse>(url, {
      params: { idGrupo: groupId },
    });

    return response.data;
  },
  TEXTS.defaultError
);
export const getCdaBasicDataById = async (id: number) => {
  // Asegúrate de que axiosGdt esté configurado con la URL base correcta
  // que incluya '/srv/spring/' si es necesario
  const url = URLs.COPA_AMIGOS.DATOS_BASICOS;

  // Probar con diferentes nombres de parámetros
  const response = await axiosGdt.get<BasicDataGDA>(url, {
    params: { id },
    // Asegurarnos de que las cabeceras CORS estén configuradas correctamente
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  return response.data;
};

export const acceptCdaInvitation = async (idCopa: number) => {
  try {
    // Usamos la URL correcta desde URLS.COPA_AMIGOS.TRATAR_POSTULANTE
    const url = URLs.COPA_AMIGOS.TRATAR_POSTULANTE;
    const params = new URLSearchParams({
      tipoAccion: '3', // ACC_ACEPTAR_INVITACION
      idGrupo: idCopa.toString(),
    });

    // Realizar la solicitud a la API
    const response = await axiosGdt.post(`${url}?${params.toString()}`);
    const data = response.data;

    // Verificar si la respuesta indica un error
    if (data && data.estado === false) {
      console.error(
        'Error al aceptar la invitación:',
        data.mensaje || 'Error desconocido'
      );
      return {
        estado: false,
        mensaje: data.mensaje || 'Error desconocido',
        codError: data.codError,
      };
    }

    // Si llegamos aquí, la respuesta fue exitosa
    return {
      estado: true,
      mensaje: 'Invitación aceptada con éxito',
      ...data,
    };
  } catch (error: any) {
    console.error('Error al aceptar la invitación:', error);

    // Si el error tiene una respuesta de la API, extraer los detalles
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      return {
        estado: false,
        mensaje: errorData.mensaje || 'Error al procesar la solicitud',
        codError: errorData.codError,
      };
    }

    // Error genérico (red, timeout, etc.)
    return {
      estado: false,
      mensaje: error.message || 'Error de conexión',
    };
  }
};

export const inviteUserById = axiosErrorHandler(
  async (idGrupo: number, idUsuarioDT: number) => {
    const params = new URLSearchParams({
      idGrupo: idGrupo.toString(),
      idUsuarioDT: idUsuarioDT.toString(),
    });

    const url = `${URLs.COPA_AMIGOS.INVITAR_DT}?${params.toString()}`;

    const response = await axiosGdt.post<
      FriendCupResponse['schemas']['ResponseBase']
    >(url);

    return response.data;
  }
);
